import React from 'react';
import {
  OldCustomField,
  OldCustomFieldOption,
  OldCustomFieldDefinition,
} from '@teetsh/app/src/types/oldCustomField';
import { Label } from '@teetsh/app/src/components/ROItemList/Label';
import tw from 'twin.macro';
import clsx from 'clsx';
import HtmlViewer from '@teetsh/app/src/components/HtmlViewer';
import { ROItemList } from '@teetsh/app/src/components/ROItemList';
import { Option } from '@teetsh/app/src/components/NewCustomFields/types';
import { Tag } from '@teetsh/app/src/components/Tag';
import { Color } from '@teetsh/app/src/types/colors';
import {
  ListsPrintView,
  PhasesPrintView,
} from '@teetsh/app/src/types/sequences';
import { useTranslation } from 'react-i18next';

interface Props {
  items: OldCustomField[];
  customFieldDefinitions?: OldCustomFieldDefinition[];
  light?: boolean;
  className?: string;
  listsPrintView?: ListsPrintView;
  view?: PhasesPrintView;
  color?: Color;
}

function TextCustomField({
  label,
  value,
  isHighlighted,
  color,
}: {
  label?: string;
  value?: string;
  isHighlighted?: boolean;
  color?: Color;
}) {
  return (
    <>
      {label && (
        <Label isHighlighted={isHighlighted} color={color}>
          {label}
        </Label>
      )}
      <HtmlViewer text={value} onChange={() => {}} print />
    </>
  );
}

function MultiTagCustomField({
  items,
  label,
  isHighlighted,
  color,
}: {
  items: Option[];
  label?: string;
  isHighlighted?: boolean;
  color?: Color;
}) {
  const tags = items.map(opt => (
    <Tag key={opt.id} color={opt.color}>
      {opt.value}
    </Tag>
  ));
  if (isHighlighted) {
    return (
      <>
        {label && (
          <Label isHighlighted color={color}>
            {label}
          </Label>
        )}
        <div>
          <div tw="flex flex-wrap gap-1">{tags}</div>
        </div>
      </>
    );
  }
  return (
    <>
      <Label>{label}</Label>
      {tags}
    </>
  );
}

function CustomField({
  customField,
  isHighlighted,
  color,
  withLabel = true,
  listsPrintView,
}: {
  customField: OldCustomField;
  isHighlighted?: boolean;
  color?: Color;
  withLabel?: boolean;
  listsPrintView?: ListsPrintView;
}) {
  const { t } = useTranslation(); // ⚠️ i18next/TS quirck if we provide namespace
  const defaultLabel = t('sequence:pdf.customFieldWithNoName');

  return (
    <>
      {customField.type === 'text' && (
        <TextCustomField
          label={
            withLabel ? customField.singularName || defaultLabel : undefined
          }
          value={customField.value as string}
          isHighlighted={isHighlighted}
          color={color}
        />
      )}
      {customField.type === 'multitag' && (
        <MultiTagCustomField
          items={
            // en print l'utilisateur n'est pas connecté, on doit donc hydrater les custom fields coté API et les utiliser tel quel coté front
            customField.value as unknown as OldCustomFieldOption[]
          }
          label={
            withLabel ? customField?.pluralName || defaultLabel : undefined
          }
          isHighlighted={isHighlighted}
          color={color}
        />
      )}
      {customField.type === 'multiselect' && (
        <ROItemList
          items={(customField?.items || []).map(v => v.value)}
          label={
            withLabel ? customField?.singularName || defaultLabel : undefined
          }
          labelPlural={
            withLabel ? customField?.pluralName || defaultLabel : undefined
          }
          isLabelHighlighted={isHighlighted}
          color={color}
          view={isHighlighted ? listsPrintView : undefined}
        />
      )}
    </>
  );
}

export function CustomFieldsList(props: Props) {
  const {
    light = false,
    className,
    items: customFields,
    listsPrintView,
    view,
    color,
  } = props;
  if (!customFields || customFields.length === 0) {
    return null;
  }
  if (view === 'table') {
    return (
      <>
        {customFields.map((cf, index) => (
          <td key={index}>
            <CustomField
              isHighlighted
              customField={cf}
              color={color}
              withLabel={false}
              listsPrintView={listsPrintView}
            />
          </td>
        ))}
      </>
    );
  }
  if (view) {
    return (
      <>
        {customFields.map(cf => (
          <CustomField
            key={cf.id}
            isHighlighted
            customField={cf}
            color={color}
            listsPrintView={listsPrintView}
          />
        ))}
      </>
    );
  }
  return (
    <div css={[!light && tw`flex flex-wrap`]} className={clsx(className)}>
      {customFields.map(cf => (
        <div
          key={cf.id}
          css={[
            !light && tw`border-l-2 pl-2 border-neutral-moderate mr-4`,
            tw`mb-1`,
          ]}
        >
          <CustomField customField={cf} />
        </div>
      ))}
    </div>
  );
}
